import type { MenuScanWarning, ScannedMenu } from '@pulsify/shared/dto'
import type { PreparedUpload } from './prepare-upload'
import { type ScanFailure, scanFailure } from './scan-failure'

type MenuScanOutcome =
  | { status: 'done'; menu: ScannedMenu; warnings: MenuScanWarning[] }
  | { status: 'unusable'; files: { name: string; reason: 'undecodable' | 'too_large' }[] }
  | { status: 'cancelled' }
  | { status: 'failed'; failure: ScanFailure }

interface MenuScanSteps {
  prepare: (file: File) => Promise<PreparedUpload>
  send: (files: File[]) => Promise<{ menu: ScannedMenu; warnings: MenuScanWarning[] }>
  signal: AbortSignal
}

/**
 * One reading from the picked files to what the page shows next. Nothing is sent while a file
 * cannot be used, and a cancel never reads as an error.
 */
export async function runMenuScan(
  files: readonly File[],
  { prepare, send, signal }: MenuScanSteps,
): Promise<MenuScanOutcome> {
  const prepared: PreparedUpload[] = []
  for (const file of files) prepared.push(await prepare(file))
  const unusable = prepared.flatMap((entry) =>
    entry.ok ? [] : [{ name: entry.name, reason: entry.reason }],
  )
  if (unusable.length > 0) return { status: 'unusable', files: unusable }
  if (signal.aborted) return { status: 'cancelled' }
  try {
    const result = await send(prepared.flatMap((entry) => (entry.ok ? [entry.file] : [])))
    return { status: 'done', ...result }
  } catch (error) {
    if (signal.aborted) return { status: 'cancelled' }
    return { status: 'failed', failure: scanFailure(error) }
  }
}
